'use client';

import React, { useState, useCallback, useMemo, useEffect, type RefObject } from 'react';
import { usePathname, useRouter, useSearchParams } from '@/lib/app-router';
import { Header } from '@/components/layout/header/Header';
import { Footer } from '@/components/layout/footer/Footer';
import { SiteHeader } from '@/components/layout/site-header';
import { useHasLiveSession } from '@/hooks/useHasLiveSession';
import { useSiteNavigation } from '@/hooks/useSiteNavigation';
import { MOBILE_MENU_ITEMS } from '@/lib/constants/NavItems';
import { useWhiteLabelNav } from '@/hooks/useWhiteLabelNav';
import { useThemeStore } from '@/stores/theme.store';
import { useAuthStore } from '@/stores/user.store';
import { resolveLogoSrc, getCompanyBookingEnablement } from '@/lib/theme-utils';
import { getContactName } from '@/lib/contact-utils';
import { isLoyaltyVisible } from '@/lib/loyalty-utils';
import { isScanEntryVisible } from '@/lib/special-rate-utils';
import { getBase64QRCode } from '@/lib/qr-code';
import { useLoyaltyApplePassUrl } from '@/hooks/shared/useLoyaltyApplePassUrl';
import { MY_ACCOUNT_ROUTES, isQrScanPath } from '@/routes/routes.constants';
import { useUserMenu } from '@/hooks/useUserMenu';
import { useLoginHref } from '@/hooks/useLoginHref';
import { useActiveSessionTimer } from '@/hooks/useActiveSessionTimer';
import { UserMenu } from '@/components/layout/user-menu';
import { LoyaltyQrModal } from '@/components/myaccount/loyalty-qr-modal';
import { ViewQRDialog } from '@/components/myaccount/manage-permit/permit-details/ViewQRDialog';
import { useWidgetStore } from '@/stores/widget.store';
import { MobileNavigation } from '@/components/myaccount/account-navigation/MobileNavigation';
import { FindSessionProvider, useFindSessionContext } from './FindSessionContext';
import { FindSessionModal } from '@/components/landing/find-session-modal';
import { ReleaseRefreshBanner } from '@/components/shared/release-refresh-banner';
import { useReleaseCheck } from '@/hooks/useReleaseCheck';
import {
  SiteChromeContext,
  type SiteChromeAccount,
  type SiteChromeKind,
  type SiteChromeValue,
} from '@/components/site-page/SiteChromeContext';

/** The public homepage. Only here may a site-page HEADER / FOOTER block replace the global bars. */
const HOME_PATH = '/';

/**
 * Keeps the release-refresh check and banner alive while the global Header is replaced by the
 * homepage's HEADER block: both normally live inside `Header`, and a visitor on `/` must still be
 * told to refresh when a new build is deployed.
 */
function HeaderlessReleaseWatch() {
  useReleaseCheck();
  return <ReleaseRefreshBanner />;
}

function PublicLayoutInner({ children }: { children: React.ReactNode }) {
  const { theme } = useThemeStore();
  const companyThemes = useThemeStore((s) => s.companyThemes);
  const { user, token, garageAccounts } = useAuthStore();
  const router = useRouter();
  const findSession = useFindSessionContext();

  // Active session indicator
  const { sessions: activeSessions, indicator: activeSessionIndicator } = useActiveSessionTimer();
  // PB-9032: the published site navigation draws the header (and footer) on every page. DEFAULT,
  // a disabled Site Builder or a failed read leave `isPublished` false: today's chrome, untouched.
  const siteNav = useSiteNavigation();
  const hasLiveSession = useHasLiveSession(user ? activeSessions : []);
  const [sessionsPopupOpen, setSessionsPopupOpen] = useState(false);
  const handleOpenSessionsPopup = useCallback(() => setSessionsPopupOpen(true), []);
  const handleCloseSessionsPopup = useCallback(() => setSessionsPopupOpen(false), []);

  const indicatorProps = useMemo(() => {
    if (!activeSessionIndicator) return null;
    return {
      ...activeSessionIndicator,
      href: undefined,
      onClick: handleOpenSessionsPopup,
    };
  }, [activeSessionIndicator, handleOpenSessionsPopup]);

  const popupProps = useMemo(() => {
    if (!activeSessionIndicator) return null;
    return {
      open: sessionsPopupOpen,
      onClose: handleCloseSessionsPopup,
      sessions: activeSessions,
    };
  }, [activeSessionIndicator, sessionsPopupOpen, handleCloseSessionsPopup, activeSessions]);

  const getDefaultWidget = useWidgetStore((state) => state.getDefaultWidget);

  useEffect(() => {
    getDefaultWidget();
  }, [getDefaultWidget]);
  const pathname = usePathname();
  const isPaymentPage = false;

  // PB-8609: a homepage HEADER / FOOTER block registers here while mounted. The global bar is
  // skipped only while one is registered AND the page is `/`, so every other route is unchanged.
  const [registeredChrome, setRegisteredChrome] = useState<Record<SiteChromeKind, number>>({
    header: 0,
    footer: 0,
  });
  const registerChrome = useCallback((kind: SiteChromeKind) => {
    setRegisteredChrome((prev) => ({ ...prev, [kind]: prev[kind] + 1 }));
    return () => setRegisteredChrome((prev) => ({ ...prev, [kind]: prev[kind] - 1 }));
  }, []);
  const onHome = pathname === HOME_PATH;
  // A published navigation owns the header everywhere, `/` included: the HEADER block yields. The
  // FOOTER block renders the navigation's footer in place, so footer suppression is unchanged.
  const suppressHeader = !siteNav.isPublished && onHome && registeredChrome.header > 0;
  const suppressFooter = onHome && registeredChrome.footer > 0;
  const showSiteHeader = siteNav.isPublished || siteNav.isLoading;

  // R2-F1: the HEADER block's own avatar ref. Kept apart from `userMenu.anchorRef`, which only the
  // global Header attaches: when the Header unmounts React nulls that ref, so sharing it with the
  // block left the account menu with no element to position against on `/`.
  const [blockAvatarRef, setBlockAvatarRef] = useState<RefObject<HTMLButtonElement | null> | null>(
    null
  );
  const registerAvatarAnchor = useCallback((ref: RefObject<HTMLButtonElement | null>) => {
    setBlockAvatarRef(ref);
    return () => setBlockAvatarRef((current) => (current === ref ? null : current));
  }, []);

  const hideFooter = isPaymentPage || pathname.startsWith('/quote/');
  // Header + footer follow the company's back-office white-label tabs.
  const { headerNavItems, footerLinks } = useWhiteLabelNav();

  const logoSrc = resolveLogoSrc(theme?.logo);
  const isLoggedIn = !!user;

  /**
   * Where the app-shell logo navigates — the desktop half of the QR logo fix (PB-4971).
   *
   * ## Invariants:
   * - On a QR scan route it points at the CURRENT scan url, QUERY STRING INCLUDED, so a parker who
   *   taps the logo restarts that scan instead of landing on the marketing home page — a dead end,
   *   because the QR code is printed on a sign in the garage rather than held on their phone. The
   *   query carries the company and direction; without it the scan cannot resolve its company.
   * - Every other route keeps `/`, unchanged.
   * - Header's own same-path handler turns this into a full re-entry of the url, which is also
   *   what closes the QR payment screen on desktop — that screen is state over the same url, and
   *   a reload drops it. Mobile cannot use that route (the brand header covers the app shell) and
   *   closes the screen explicitly via `useQrBookingPage`'s `onLogoClick`.
   */
  const searchParams = useSearchParams();
  const logoHref = useMemo(() => {
    if (!isQrScanPath(pathname)) return '/';
    const query = searchParams?.toString();
    return query ? `${pathname}?${query}` : pathname;
  }, [pathname, searchParams]);

  /**
   * Guest "Sign In" for the app-shell header — desktop nav AND the mobile guest action.
   *
   * Was a module-level constant holding a bare `/login`, which is why signing in from the header
   * on a scan or touchless surface dropped the parker on /myaccount instead of the page they left
   * (PB-4971). It has to be built per render because the return URL is the CURRENT location.
   */
  const loginHref = useLoginHref();
  const publicSecondaryNav = useMemo(
    () => [{ labelKey: 'signIn', label: 'Sign In', href: loginHref }],
    [loginHref]
  );

  const [showLoyaltyQr, setShowLoyaltyQr] = useState(false);
  const handleOpenLoyaltyQr = useCallback(() => setShowLoyaltyQr(true), []);
  const handleCloseLoyaltyQr = useCallback(() => setShowLoyaltyQr(false), []);

  const [showAccessCardQr, setShowAccessCardQr] = useState(false);
  const handleOpenAccessCardQr = useCallback(() => setShowAccessCardQr(true), []);
  const handleCloseAccessCardQr = useCallback(() => setShowAccessCardQr(false), []);

  const firstBarcodeCredential = useMemo(() => {
    for (const ga of garageAccounts) {
      for (const permit of ga.monthlyAccount?.monthlyPermits ?? []) {
        const cred = permit.accessCredentials?.find((c) => c.credentialType === 'BARCODE');
        if (cred) {
          return {
            cardNumber: cred.serialNumber || cred.cardNumber || '',
            description: cred.description ?? '',
          };
        }
      }
    }
    return null;
  }, [garageAccounts]);

  const userMenu = useUserMenu({
    onScanTicket: () => findSession.openModal('scanQr', 'scan'),
    onLoyaltyQr: handleOpenLoyaltyQr,
    onAccessCardQr: handleOpenAccessCardQr,
  });

  const userDisplayName = isLoggedIn ? getContactName(user?.contactInfo) || 'User' : undefined;

  /**
   * The element the UserMenu dropdown opens under.
   *
   * ## Invariants:
   * - While a HEADER block replaces the global Header on `/`, it is the block's avatar; otherwise
   *   it is the global Header's (`userMenu.anchorRef`), exactly as before PB-8609.
   * - The two refs are never attached by the same element pair, so neither header mounting or
   *   unmounting can null the other's anchor.
   */
  const userMenuAnchorRef = suppressHeader && blockAvatarRef ? blockAvatarRef : userMenu.anchorRef;

  const loyaltyEnabled = isLoggedIn && isLoyaltyVisible(user, theme);
  const showScanTicket = isScanEntryVisible(user, theme, companyThemes);
  const cardNumber = user?.cardNumber || '';
  const lcnDisplay =
    cardNumber && cardNumber !== '-'
      ? cardNumber.toUpperCase().startsWith('LCN-')
        ? cardNumber
        : `LCN-${cardNumber}`
      : '-';
  const largeQrSrc = useMemo(() => {
    if (!showLoyaltyQr || !cardNumber || cardNumber === '-') return '';
    return getBase64QRCode(cardNumber, 'M', 250, 15);
  }, [showLoyaltyQr, cardNumber]);

  const applePassUrl = useLoyaltyApplePassUrl(token);

  const googlePassJwt = useMemo(() => {
    const companyId = theme?.companyId;
    if (!user?.googlePassTokenMap || !companyId) return undefined;
    return user.googlePassTokenMap[companyId];
  }, [user, theme?.companyId]);

  // What the homepage HEADER block reuses from this layout: the logo, the avatar toggle, a way to
  // hand over its own avatar ref (the UserMenu below anchors to it while the Header is
  // suppressed), and the active-session pill + popup. The release check stays alive via
  // HeaderlessReleaseWatch.
  const chromeAccount = useMemo<SiteChromeAccount>(
    () => ({
      isLoggedIn,
      userName: userDisplayName,
      onAvatarClick: isLoggedIn ? userMenu.toggle : undefined,
      isMenuOpen: userMenu.isOpen,
      loginHref,
      logoSrc,
      logoAlt: theme?.companyName || 'Company logo',
    }),
    [
      isLoggedIn,
      userDisplayName,
      userMenu.toggle,
      userMenu.isOpen,
      loginHref,
      logoSrc,
      theme?.companyName,
    ]
  );
  const siteChrome = useMemo<SiteChromeValue>(
    () => ({
      registerChrome,
      account: chromeAccount,
      registerAvatarAnchor,
      siteNavPublished: siteNav.isPublished,
      siteNavFooterSlots: siteNav.isPublished ? siteNav.footerSlots : null,
      // The same props, with the same signed-in gate, as the global <Header> below: a HEADER block
      // that replaces it on `/` must still show the active-session pill and its popup.
      activeSessionIndicator: isLoggedIn ? indicatorProps : null,
      activeSessionsPopup: isLoggedIn ? popupProps : null,
    }),
    [
      registerChrome,
      chromeAccount,
      registerAvatarAnchor,
      siteNav.isPublished,
      siteNav.footerSlots,
      isLoggedIn,
      indicatorProps,
      popupProps,
    ]
  );

  return (
    <div className="tw:flex tw:flex-col tw:h-dvh tw:overflow-hidden">
      {suppressHeader && !isPaymentPage && <HeaderlessReleaseWatch />}
      {!isPaymentPage && !suppressHeader && showSiteHeader && (
        <SiteHeader
          logo={<img src={logoSrc} alt={theme?.companyName || 'Company logo'} className="tw:h-8" />}
          logoHref={logoHref}
          user={isLoggedIn ? { name: userDisplayName! } : undefined}
          onAvatarClick={isLoggedIn ? userMenu.toggle : undefined}
          avatarRef={isLoggedIn ? userMenu.anchorRef : undefined}
          isMenuOpen={userMenu.isOpen}
          activeSessionIndicator={isLoggedIn ? indicatorProps : null}
          activeSessionsPopup={isLoggedIn ? popupProps : null}
          zones={siteNav.zones}
          showLogin={siteNav.showLogin}
          loginHref={loginHref}
          hasLiveSession={hasLiveSession}
          isLoading={!siteNav.isPublished}
          className="tw:sticky tw:top-0 tw:z-20 tw:scan:z-50 tw:flex-none"
        />
      )}
      {!isPaymentPage && !suppressHeader && !showSiteHeader && (
        <Header
          logo={<img src={logoSrc} alt={theme?.companyName || 'Company logo'} className="tw:h-8" />}
          logoHref={logoHref}
          primaryNavItems={headerNavItems}
          secondaryNavItems={isLoggedIn ? [] : publicSecondaryNav}
          mobileGuestAction={publicSecondaryNav[0]}
          user={isLoggedIn ? { name: userDisplayName! } : undefined}
          onAvatarClick={isLoggedIn ? userMenu.toggle : undefined}
          avatarRef={isLoggedIn ? userMenu.anchorRef : undefined}
          isMenuOpen={userMenu.isOpen}
          activeSessionIndicator={isLoggedIn ? indicatorProps : null}
          activeSessionsPopup={isLoggedIn ? popupProps : null}
          className="tw:sticky tw:top-0 tw:z-20 tw:scan:z-50 tw:flex-none"
        />
      )}
      {isLoggedIn && (
        <UserMenu
          open={userMenu.isOpen}
          userName={userDisplayName!}
          onClose={userMenu.close}
          onProfileClick={userMenu.handleProfileClick}
          showAccessCardQr={!!firstBarcodeCredential}
          showLoyaltyQr={loyaltyEnabled}
          showScanTicket={showScanTicket}
          onAccessCardQrClick={userMenu.handleAccessCardQrClick}
          onLoyaltyQrClick={userMenu.handleLoyaltyQrClick}
          onScanTicketClick={userMenu.handleScanTicketClick}
          onOverviewClick={userMenu.handleOverviewClick}
          onChangeAppearanceClick={userMenu.handleChangeAppearanceClick}
          onLogoutClick={userMenu.handleLogoutClick}
          anchorRef={userMenuAnchorRef}
        />
      )}
      <main
        id="main-content"
        className="tw:flex-1 tw:flex tw:flex-col tw:relative tw:min-h-0 tw:overflow-hidden"
      >
        <SiteChromeContext.Provider value={siteChrome}>{children}</SiteChromeContext.Provider>
      </main>
      {isLoggedIn && !hideFooter && (
        <MobileNavigation
          items={MOBILE_MENU_ITEMS.filter(
            (item) => item.id !== 'reservations' || getCompanyBookingEnablement(theme).hourly
          )}
          activeItemId="home"
          onItemClick={(itemId) => {
            switch (itemId) {
              case 'home':
                router.push('/');
                break;
              case 'reservations':
                router.push(MY_ACCOUNT_ROUTES.reservations);
                break;
              case 'account':
                router.push(MY_ACCOUNT_ROUTES.overview);
                break;
            }
          }}
          className="tw:md:hidden tw:flex-shrink-0"
        />
      )}
      {!hideFooter && !suppressFooter && (
        <Footer
          companyName={theme?.companyName ?? ''}
          links={footerLinks}
          className="tw:hidden tw:scan:flex tw:flex-none"
        />
      )}
      {loyaltyEnabled && showLoyaltyQr && (
        <LoyaltyQrModal
          logo={theme?.logo}
          companyName={theme?.companyName}
          userName={userDisplayName!}
          lcnDisplay={lcnDisplay}
          largeQrSrc={largeQrSrc}
          onClose={handleCloseLoyaltyQr}
          applePassUrl={applePassUrl}
          googlePassJwt={googlePassJwt}
        />
      )}
      {isLoggedIn && firstBarcodeCredential && showAccessCardQr && (
        <ViewQRDialog
          open={showAccessCardQr}
          onClose={handleCloseAccessCardQr}
          description={firstBarcodeCredential.description}
          cardNumber={firstBarcodeCredential.cardNumber}
        />
      )}
      {isLoggedIn && (
        <FindSessionModal
          open={findSession.open}
          onClose={findSession.closeModal}
          initialTab={findSession.initialTab}
          entry={findSession.entry}
        />
      )}
    </div>
  );
}

export default function PublicLayoutClient({ children }: { children: React.ReactNode }) {
  return (
    <FindSessionProvider>
      <PublicLayoutInner>{children}</PublicLayoutInner>
    </FindSessionProvider>
  );
}
